import { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaPlus, FaTrash, FaEdit, FaSave, FaCog, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import AutomationRulesModal from './AutomationRulesModal';
import InternalNotePermissionsTab from './InternalNotePermissionsTab';
import BoardSettingsPermissionsTab from './BoardSettingsPermissionsTab';
import { fetchBoard } from '@/services/kanbanBoardService';
import { FaIcon, FA_ICON_MAP } from '@/utils/faIconMap';
import '@/styles/ActionItemsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * BoardSettingsModal Component
 * Allows editing board name, description, columns, swimlanes, and other settings.
 * Fetches full board details (including columns) on mount via the authenticated
 * fetchBoard API, since the boards list endpoint does not include column data.
 */
const BoardSettingsModal = ({ board, onClose, onSave, onDelete }) => {
    const { apiFetch } = useAuth();
    const [activeTab, setActiveTab] = useState('general');
    const [boardName, setBoardName] = useState(board?.name || '');
    const [boardDescription, setBoardDescription] = useState(board?.description || '');
    const [boardColor, setBoardColor] = useState(board?.color || '#3498db');
    const [boardIcon, setBoardIcon] = useState(board?.icon || 'fa-clipboard');
    const [isPublic, setIsPublic] = useState(board?.isPublic || false);
    const [columns, setColumns] = useState(board?.columns || []);
    const [loadingColumns, setLoadingColumns] = useState(true);
    const [editingColumnId, setEditingColumnId] = useState(null);
    const [newColumn, setNewColumn] = useState(null);
    const [enableDuplicateDetection, setEnableDuplicateDetection] = useState(board?.enableDuplicateDetection || false);

    // Fetch full board details (including columns) on mount.
    // The boards list endpoint (GetBoards) does not include column data,
    // so we must call the single-board detail endpoint (GetBoard) to load them.
    const loadBoardDetails = useCallback(async () => {
        if (!board?.boardId) {
            setLoadingColumns(false);
            return;
        }
        try {
            const boardDetails = await fetchBoard(apiFetch, board.boardId);
            if (boardDetails?.columns && boardDetails.columns.length > 0) {
                setColumns(boardDetails.columns);
            }
            // Sync enableDuplicateDetection from the authoritative board detail
            // response — the boards list prop may not have this value populated
            // at mount time, leaving the checkbox incorrectly unticked.
            if (boardDetails?.enableDuplicateDetection !== undefined) {
                setEnableDuplicateDetection(boardDetails.enableDuplicateDetection);
            }
        } catch (error) {
            // If the board can't be fetched (e.g. disabled/deleted), proceed with
            // empty columns so the user can still manage or delete the board.
            Sentry.captureException(error);
            console.warn('Could not load board details; proceeding with empty columns:', error);
        } finally {
            setLoadingColumns(false);
        }
    }, [apiFetch, board.boardId]);

    useEffect(() => {
        (async () => {
            await loadBoardDetails();
        })();
    }, [loadBoardDetails]);

    // Icon picker state
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [iconSearchQuery, setIconSearchQuery] = useState('');

    const [saving, setSaving] = useState(false);

    // Common status values for action items
    const STATUS_OPTIONS = [
        { value: 'Open', label: 'Open' },
        { value: 'Pending', label: 'Pending/In Progress' },
        { value: 'On-hold', label: 'On Hold' },
        { value: 'Solved', label: 'Solved/Done' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Closed', label: 'Closed' }
    ];

    // Color presets for columns
    const COLOR_PRESETS = [
        '#f39c12', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6',
        '#1abc9c', '#34495e', '#95a5a6', '#e67e22', '#16a085'
    ];

    // All available icons from the FA_ICON_MAP for the icon picker
    const availableIcons = Object.keys(FA_ICON_MAP);

    // Filter icons by search query
    const filteredIcons = iconSearchQuery
        ? availableIcons.filter(iconKey => iconKey.toLowerCase().includes(iconSearchQuery.toLowerCase()))
        : availableIcons;

    // Column management functions
    const handleAddColumn = () => {
        setNewColumn({
            name: '',
            statusValue: '',
            color: '#3498db',
            sortOrder: columns.length,
            allowCreate: true,
            isCompletedColumn: false,
            isDefaultColumn: false,
            wipLimit: 0
        });
    };

    const handleSaveNewColumn = () => {
        if (!newColumn.name.trim()) {
            toast.error('Column name is required');
            return;
        }
        const resolvedStatus = newColumn.statusValue.trim() || newColumn.name.trim();
        const isDuplicate = columns.some(col => col.statusValue === resolvedStatus);
        if (isDuplicate) {
            toast.error(`Status "${resolvedStatus}" is already used by another column. Each column must have a unique status value.`);
            return;  // ← hard block, was missing before
        }
        setColumns([...columns, { ...newColumn, statusValue: resolvedStatus, columnId: `temp-${Date.now()}` }]);
        setNewColumn(null);
    };

    const handleUpdateColumn = (columnId, updates) => {
        // Enforce single-default: when marking a column as default, clear others
        if (updates.isDefaultColumn === true) {
            setColumns(columns.map(col =>
                col.columnId === columnId
                    ? { ...col, ...updates }
                    : { ...col, isDefaultColumn: false }
            ));
        } else {
            setColumns(columns.map(col =>
                col.columnId === columnId ? { ...col, ...updates } : col
            ));
        }
    };

    const handleDeleteColumn = (columnId) => {
        if (columns.length <= 1) {
            toast.error('Board must have at least one column');
            return;
        }

        if (window.confirm('Are you sure you want to delete this column?')) {
            setColumns(columns.filter(col => col.columnId !== columnId));
        }
    };

    const handleMoveColumn = (columnId, direction) => {
        const index = columns.findIndex(col => col.columnId === columnId);
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === columns.length - 1)
        ) {
            return;
        }

        const newColumns = [...columns];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        [newColumns[index], newColumns[targetIndex]] = [newColumns[targetIndex], newColumns[index]];

        // Update sort orders
        newColumns.forEach((col, idx) => {
            col.sortOrder = idx;
        });

        setColumns(newColumns);
    };

    const handleSave = async () => {
        if (!boardName.trim()) {
            toast.error('Board name is required');
            return;
        }

        if (columns.length === 0) {
            toast.error('Board must have at least one column');
            return;
        }

        setSaving(true);
        try {
            await onSave(board.boardId, {
                name: boardName,
                description: boardDescription,
                color: boardColor,
                icon: boardIcon,
                isPublic: isPublic,
                enableDuplicateDetection: enableDuplicateDetection,   
                columns: columns.map((col, idx) => ({
                    ...col,
                    sortOrder: idx
                }))
            });
            toast.success('Board settings saved successfully');
            onClose();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to save board settings');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBoard = () => {
        if (window.confirm(`Are you sure you want to delete "${board.name}"? This action cannot be undone.`)) {
            onDelete(board.boardId);
        }
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Board Settings</h2>
                        <button className="close-button" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="settings-tabs">
                        <button
                            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
                            onClick={() => setActiveTab('general')}
                        >
                            General
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'columns' ? 'active' : ''}`}
                            onClick={() => setActiveTab('columns')}
                        >
                            Columns
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'automation' ? 'active' : ''}`}
                            onClick={() => setActiveTab('automation')}
                        >
                            <FaCog /> Automation
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'internalNotes' ? 'active' : ''}`}
                            onClick={() => setActiveTab('internalNotes')}
                        >
                            🔒 Internal Notes
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'accessControl' ? 'active' : ''}`}
                            onClick={() => setActiveTab('accessControl')}
                        >
                            🛡️ Access Control
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* General Settings Tab */}
                        {activeTab === 'general' && (
                            <div className="settings-section">
                                <h3>Basic Information</h3>

                                <div className="form-group">
                                    <label htmlFor="boardName">Board Name *</label>
                                    <input
                                        id="boardName"
                                        type="text"
                                        value={boardName}
                                        onChange={(e) => setBoardName(e.target.value)}
                                        placeholder="e.g., Marketing Tasks, IT Support Queue"
                                        maxLength={200}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="boardDescription">Description</label>
                                    <textarea
                                        id="boardDescription"
                                        value={boardDescription}
                                        onChange={(e) => setBoardDescription(e.target.value)}
                                        placeholder="Optional description of this board's purpose"
                                        rows={3}
                                        maxLength={1000}
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="boardIcon">Icon</label>
                                        <div className="icon-picker-trigger">
                                            <button
                                                type="button"
                                                className="icon-picker-button"
                                                onClick={() => setShowIconPicker(!showIconPicker)}
                                            >
                                                <FaIcon icon={boardIcon} size={20} />
                                                <span className="icon-name">{boardIcon}</span>
                                            </button>
                                            {showIconPicker && (
                                                <div className="icon-picker-dropdown">
                                                    <div className="icon-picker-search">
                                                        <input
                                                            type="text"
                                                            value={iconSearchQuery}
                                                            onChange={(e) => setIconSearchQuery(e.target.value)}
                                                            placeholder="Search icons..."
                                                            autoFocus
                                                        />
                                                    </div>
                                                    <div className="icon-picker-grid">
                                                        {filteredIcons.slice(0, 60).map(iconKey => (
                                                            <button
                                                                key={iconKey}
                                                                type="button"
                                                                className={`icon-picker-item ${boardIcon === iconKey ? 'selected' : ''}`}
                                                                onClick={() => {
                                                                    setBoardIcon(iconKey);
                                                                    setShowIconPicker(false);
                                                                    setIconSearchQuery('');
                                                                }}
                                                                title={iconKey}
                                                            >
                                                                <FaIcon icon={iconKey} size={18} />
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {filteredIcons.length > 60 && (
                                                        <div className="icon-picker-hint">
                                                            Showing 60 of {filteredIcons.length} — refine your search
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="boardColor">Color</label>
                                        <div className="color-picker">
                                            <input
                                                id="boardColor"
                                                type="color"
                                                value={boardColor}
                                                onChange={(e) => setBoardColor(e.target.value)}
                                            />
                                            <div className="color-presets">
                                                {COLOR_PRESETS.map(color => (
                                                    <button
                                                        key={color}
                                                        className={`color-preset ${boardColor === color ? 'active' : ''}`}
                                                        style={{ backgroundColor: color }}
                                                        onClick={() => setBoardColor(color)}
                                                        title={color}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={isPublic}
                                            onChange={(e) => setIsPublic(e.target.checked)}
                                        />
                                        <span>Make this board public (visible to all users)</span>
                                    </label>
                                    <small>Public boards are read-only unless you explicitly grant edit access</small>
                                </div>

                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={enableDuplicateDetection}
                                            onChange={(e) => setEnableDuplicateDetection(e.target.checked)}
                                        />
                                        <span>Enable duplicate detection</span>
                                    </label>
                                    <small>When enabled, a banner will highlight action items on this board that appear to be duplicates and allow one-click merging</small>
                                </div>
                            </div>
                        )}

                        {/* Column Management Tab - Vertical layout with drag-and-drop reorder */}
                        {activeTab === 'columns' && (
                            <div className="settings-section">
                                <div className="section-header">
                                    <h3>Columns</h3>
                                    <button className="btn-primary-small" onClick={handleAddColumn} disabled={loadingColumns}>
                                        <FaPlus /> Add Column
                                    </button>
                                </div>
                                <p className="section-description">
                                    Drag columns up or down to reorder. Columns map to action item statuses.
                                </p>

                                {loadingColumns ? (
                                    <div className="loading-indicator">Loading columns...</div>
                                ) : (
                                <div className="columns-list vertical">
                                    {columns.map((column, index) => (
                                        <div key={column.columnId} className="column-item vertical">
                                            {editingColumnId === column.columnId ? (
                                                <div className="column-edit-form vertical-form">
                                                    <input
                                                        type="text"
                                                        value={column.name}
                                                        onChange={(e) => handleUpdateColumn(column.columnId, { name: e.target.value })}
                                                        placeholder="Column name"
                                                    />
                                                    <input
                                                        type="text"
                                                        list="status-suggestions-edit"
                                                        value={column.statusValue}
                                                        onChange={(e) => handleUpdateColumn(column.columnId, { statusValue: e.target.value })}
                                                        placeholder="e.g. In Review, Waiting, QA"
                                                    />
                                                    <datalist id="status-suggestions-edit">
                                                        {STATUS_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </datalist>
                                                    <input
                                                        type="color"
                                                        value={column.color || '#3498db'}
                                                        onChange={(e) => handleUpdateColumn(column.columnId, { color: e.target.value })}
                                                    />
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }} title="New items land in this column by default">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!column.isDefaultColumn}
                                                            onChange={(e) => handleUpdateColumn(column.columnId, { isDefaultColumn: e.target.checked })}
                                                        />
                                                        Default for new items
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }} title="Items in this column are considered completed/terminal and excluded from attention notifications and nudges">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!column.isCompletedColumn}
                                                            onChange={(e) => handleUpdateColumn(column.columnId, { isCompletedColumn: e.target.checked })}
                                                        />
                                                        Completed / Terminal
                                                    </label>
                                                    <button className="btn-icon" onClick={() => setEditingColumnId(null)} title="Done editing">
                                                        <FaSave />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="column-reorder-controls">
                                                        <button
                                                            className="btn-icon-small"
                                                            onClick={() => handleMoveColumn(column.columnId, 'up')}
                                                            disabled={index === 0}
                                                            title="Move up"
                                                        >
                                                            <FaArrowUp />
                                                        </button>
                                                        <button
                                                            className="btn-icon-small"
                                                            onClick={() => handleMoveColumn(column.columnId, 'down')}
                                                            disabled={index === columns.length - 1}
                                                            title="Move down"
                                                        >
                                                            <FaArrowDown />
                                                        </button>
                                                    </div>
                                                    <div className="column-display vertical-display">
                                                        <div
                                                            className="column-color-indicator"
                                                            style={{ backgroundColor: column.color || '#3498db' }}
                                                        />
                                                        <div className="column-info">
                                                            <span className="column-name">{column.name}</span>
                                                            <span className="column-status">{column.statusValue}</span>
                                                            {column.isDefaultColumn && (
                                                                <span className="column-badge" title="New items land here by default" style={{ fontSize: '0.7rem', background: '#27ae60', color: '#fff', borderRadius: '3px', padding: '1px 5px', marginLeft: '4px' }}>Default</span>
                                                            )}
                                                            {column.isCompletedColumn && (
                                                                <span className="column-badge" title="Items here are considered completed/terminal" style={{ fontSize: '0.7rem', background: '#95a5a6', color: '#fff', borderRadius: '3px', padding: '1px 5px', marginLeft: '4px' }}>Terminal</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="column-actions">
                                                        <button
                                                            className="btn-icon-small"
                                                            onClick={() => setEditingColumnId(column.columnId)}
                                                            title="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn-icon-small btn-danger"
                                                            onClick={() => handleDeleteColumn(column.columnId)}
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}

                                    {newColumn && (
                                        <div className="column-item vertical new-column">
                                            <div className="column-edit-form vertical-form">
                                                <input
                                                    type="text"
                                                    value={newColumn.name}
                                                    onChange={(e) => setNewColumn({ ...newColumn, name: e.target.value })}
                                                    placeholder="Column name"
                                                    autoFocus
                                                />
                                                <input
                                                    type="text"
                                                    list="status-suggestions"
                                                    value={newColumn.statusValue}
                                                    onChange={(e) => setNewColumn({ ...newColumn, statusValue: e.target.value })}
                                                    placeholder="e.g. In Review, Waiting, QA"
                                                />
                                                <datalist id="status-suggestions">
                                                    {STATUS_OPTIONS.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </datalist>
                                                <input
                                                    type="color"
                                                    value={newColumn.color}
                                                    onChange={(e) => setNewColumn({ ...newColumn, color: e.target.value })}
                                                />
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }} title="New items land in this column by default">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!newColumn.isDefaultColumn}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            // Enforce single-default: clear other columns if this one is set as default
                                                            if (checked) {
                                                                setColumns(columns.map(col => ({ ...col, isDefaultColumn: false })));
                                                            }
                                                            setNewColumn({ ...newColumn, isDefaultColumn: checked });
                                                        }}
                                                    />
                                                    Default for new items
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }} title="Items in this column are considered completed/terminal and excluded from attention notifications and nudges">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!newColumn.isCompletedColumn}
                                                        onChange={(e) => setNewColumn({ ...newColumn, isCompletedColumn: e.target.checked })}
                                                    />
                                                    Completed / Terminal
                                                </label>
                                                <button className="btn-primary-small" onClick={handleSaveNewColumn}>
                                                    <FaSave /> Save
                                                </button>
                                                <button className="btn-secondary-small" onClick={() => setNewColumn(null)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                )}
                            </div>
                        )}

                        {/* Automation Tab — renders rules inline instead of a separate modal */}
                        {activeTab === 'automation' && (
                            <div className="settings-section automation-inline-section">
                                {loadingColumns ? (
                                    <div className="loading-indicator">Loading board columns...</div>
                                ) : (
                                    <AutomationRulesModal
                                        board={board}
                                        boardColumns={columns}
                                        onClose={() => setActiveTab('general')}
                                        onRulesUpdated={() => {
                                            // Optionally refresh data
                                        }}
                                        inline={true}
                                    />
                                )}
                            </div>
                        )}

                        {/* Internal Notes Permission Configuration Tab */}
                        {activeTab === 'internalNotes' && (
                            <InternalNotePermissionsTab boardId={board.boardId} />
                        )}

                        {/* Access Control Tab - Settings Permissions */}
                        {activeTab === 'accessControl' && (
                            <BoardSettingsPermissionsTab boardId={board.boardId} />
                        )}
                    </div>

                    <div className="modal-footer">
                        <div className="footer-left">
                            {!board.isDefault && activeTab !== 'automation' && activeTab !== 'swimlanes' && activeTab !== 'internalNotes' && activeTab !== 'accessControl' && (
                                <button
                                    className="btn-danger"
                                    onClick={handleDeleteBoard}
                                    disabled={saving}
                                >
                                    <FaTrash /> Delete Board
                                </button>
                            )}
                        </div>
                        <div className="footer-right">
                            <button className="btn-secondary" onClick={onClose} disabled={saving}>
                                Cancel
                            </button>
                            {activeTab !== 'automation' && activeTab !== 'swimlanes' && activeTab !== 'internalNotes' && activeTab !== 'accessControl' && (
                                <button className="btn-primary" onClick={handleSave} disabled={saving || loadingColumns}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            )}
                            {(activeTab === 'swimlanes' || activeTab === 'internalNotes' || activeTab === 'accessControl') && (
                                <button className="btn-primary" onClick={onClose}>
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Automation Rules Modal */}
        </>
    );
};

export default BoardSettingsModal;